/**
 * SheetLightbox.js
 *
 * Full-screen lightbox that pops a single A4 sheet into a large, comfortable
 * reading view with prev/next page navigation.
 * Supports dynamic 8-card vs 6-card slot display.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { CompactNoteBlock, ExpandedNoteBlock } from './NoteBlock';
import styles from './SheetLightbox.module.css';

// Native miniature paper dimensions (must match A4Sheet.module.css .paper)
const PAPER_W = 280;
const PAPER_H = Math.round(PAPER_W * 1.414); // 396px

/** Compute the CSS scale so the paper fits within ~82 vh / available width */
function computeScale() {
  const NAV_RESERVE = 140; // space for two arrow buttons
  const availH = window.innerHeight * 0.82;
  const availW = (window.innerWidth - NAV_RESERVE) * 0.92;
  return Math.min(availH / PAPER_H, availW / PAPER_W, 2.6);
}

export default function SheetLightbox({
  pages,          // NoteBlock[][]  — all pages
  activeIndex,    // number         — which page is shown
  onNavigate,     // (delta: -1 | 1) => void
  onClose,        // () => void
  onDelete,       // (id) => void
  onSave,         // (id, fields) => void
  isUnlocked,
  onLockedAction,
  subjectTitle = 'Study',
}) {
  const [expandedNote, setExpandedNote] = useState(null);
  const [scale, setScale] = useState(computeScale);

  const notes = useMemo(() => pages[activeIndex] || [], [pages, activeIndex]); const totalPages = pages.length;

  const targetCapacity = notes.length > 6 ? 8 : 6;

  // Fill grid to target capacity
  const filledSlots = useMemo(() => {
    const slots = [...notes];
    while (slots.length < targetCapacity) slots.push(null);
    return slots;
  }, [notes, targetCapacity]);

  // ── Resize handler ────────────────────────────────────────────────────
  useEffect(() => {
    const update = () => setScale(computeScale());
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // ── Keyboard: Esc closes; Arrow keys navigate ─────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (expandedNote) { setExpandedNote(null); return; }
        onClose();
      }
      if (e.key === 'ArrowLeft' && !expandedNote && activeIndex > 0) { navigate(-1); }
      if (e.key === 'ArrowRight' && !expandedNote && activeIndex < totalPages - 1) { navigate(1); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [expandedNote, activeIndex, totalPages, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const navigate = useCallback((delta) => {
    setExpandedNote(null);
    onNavigate(delta);
  }, [onNavigate]);

  const handleSave = (id, fields) => { onSave(id, fields); setExpandedNote(null); };
  const handleDelete = (id) => { onDelete(id); setExpandedNote(null); };

  // Scaled outer container dimensions
  const scaledW = PAPER_W * scale;
  const scaledH = PAPER_H * scale;

  return (
    <>
      {/* ── Backdrop + scaled paper + nav ───────────────────────────────── */}
      <motion.div
        className={styles.backdrop}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        onClick={onClose}
      >
        {/* Left arrow */}
        <motion.button
          className={`${styles.navBtn} ${activeIndex === 0 ? styles.navBtnHidden : ''}`}
          onClick={(e) => { e.stopPropagation(); navigate(-1); }}
          whileHover={{ scale: 1.12 }}
          whileTap={{ scale: 0.92 }}
          aria-label="Previous page"
        >
          ←
        </motion.button>

        {/* Scaled A4 paper */}
        <motion.div
          className={styles.scaledOuter}
          style={{ width: scaledW, height: scaledH }}
          initial={{ scale: 0.55, opacity: 0, y: 24 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.82, opacity: 0, y: 12 }}
          transition={{ type: 'spring', stiffness: 320, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
        >
          <LayoutGroup id={`lightbox-${activeIndex}`}>
            <div
              className={styles.scaledInner}
              style={{
                width: PAPER_W,
                height: PAPER_H,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              {/* Decorative paper header */}
              <div className={styles.paperHeader}>
                <span className={styles.paperHeaderTitle}>{subjectTitle} Notes</span>
                <span className={styles.paperHeaderPage}>Page {activeIndex + 1} / {totalPages} ({notes.length} notes)</span>
              </div>

              {/* Note grid */}
              {notes.length === 0 ? (
                <div className={styles.emptyPaper}>
                  <span className={styles.emptyPaperIcon}>📄</span>
                  <span className={styles.emptyPaperText}>Empty page</span>
                </div>
              ) : (
                <div className={`${styles.noteGrid} ${targetCapacity === 8 ? styles.noteGrid8 : ''}`}>
                  {filledSlots.map((note, i) =>
                    note ? (
                      <CompactNoteBlock
                        key={note.id}
                        note={note}
                        layoutIdPrefix="lb-"
                        onExpand={(n) => { setExpandedNote(n); }}
                        onDelete={isUnlocked ? handleDelete : onLockedAction}
                      />
                    ) : (
                      <div key={`empty-${i}`} className={styles.emptySlot} />
                    )
                  )}
                </div>
              )}
            </div>
          </LayoutGroup>

          {/* Close button */}
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close lightbox (Esc)"
          >
            ✕
          </button>

          {/* Dot page indicator */}
          {totalPages > 1 && (
            <div className={styles.dotsRow}>
              {pages.map((_, i) => (
                <button
                  key={i}
                  className={`${styles.dot} ${i === activeIndex ? styles.dotActive : ''}`}
                  onClick={(e) => { e.stopPropagation(); if (i !== activeIndex) { setExpandedNote(null); onNavigate(i - activeIndex); } }}
                  aria-label={`Go to page ${i + 1}`}
                />
              ))}
            </div>
          )}

          {/* Page badge */}
          <div className={styles.pageBadgeWrap}>
            <div className={styles.pageBadge}>
              Page {activeIndex + 1}
            </div>
          </div>
        </motion.div>

        {/* Right arrow */}
        <motion.button
          className={`${styles.navBtn} ${activeIndex === totalPages - 1 ? styles.navBtnHidden : ''}`}
          onClick={(e) => { e.stopPropagation(); navigate(1); }}
          whileHover={{ scale: 1.12 }}
          whileTap={{ scale: 0.92 }}
          aria-label="Next page"
        >
          →
        </motion.button>

        {/* Keyboard hint */}
        <div className={styles.escHint}>
          Esc to close · ← → to navigate · click card to edit
        </div>
      </motion.div>

      {/* ── Expanded note overlay ─────────────────────────────────────── */}
      <AnimatePresence>
        {expandedNote && (
          <ExpandedNoteBlock
            key={expandedNote.id}
            note={expandedNote}
            pageIndex={activeIndex}
            onSave={handleSave}
            onDelete={handleDelete}
            onClose={() => setExpandedNote(null)}
            noLayoutId
            isUnlocked={isUnlocked}
            onLockedAction={onLockedAction}
            subjectTitle={subjectTitle}
          />
        )}
      </AnimatePresence>
    </>
  );
}
